import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChangeIndicatorCompact } from "./ChangeIndicator";
import { CommentsIcon } from "@/assets/icons/comments";
import { FbLikeIcon } from "@/assets/icons/fb-like";
import { FbLoveIcon } from "@/assets/icons/fb-love";
import { ShareIcon } from "@/assets/icons/share";
import type { PulseSnapshotData } from "../../types/pulse.types";

interface PulseSnapshotChangesProps {
  snapshot: PulseSnapshotData | undefined;
  isLoading: boolean;
}

export function PulseSnapshotChanges({
  snapshot,
  isLoading,
}: PulseSnapshotChangesProps) {
  if (isLoading) {
    return (
      <Card className="flex w-full flex-col !gap-3 !p-4 !shadow-none">
        <CardHeader>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-3 w-36" />
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!snapshot) {
    return (
      <Card className="flex w-full flex-col !gap-3 !p-4 !shadow-none">
        <CardHeader>
          <CardTitle className="!leading-9 !-tracking-wide">
            Engagement Changes
          </CardTitle>
        </CardHeader>
        <p className="text-sm text-neutral-500">No snapshot data available</p>
      </Card>
    );
  }

  const currentEngagements = snapshot.current_snapshot.engagements;
  const previousEngagements = snapshot.previous_snapshot?.engagements;

  const currentLikes =
    (currentEngagements.soc_like_count || 0) +
    (currentEngagements.soc_love_count || 0);
  const currentComments = currentEngagements.soc_comment_count || 0;
  const currentShares = currentEngagements.soc_share_count || 0;
  const currentViews = currentEngagements.soc_view_count || 0;

  const previousLikes = previousEngagements
    ? (previousEngagements.soc_like_count || 0) +
      (previousEngagements.soc_love_count || 0)
    : currentLikes;
  const previousComments =
    previousEngagements?.soc_comment_count ?? currentComments;
  const previousShares = previousEngagements?.soc_share_count ?? currentShares;
  const previousViews = previousEngagements?.soc_view_count ?? currentViews;

  const likesChange = currentLikes - previousLikes;
  const commentsChange = currentComments - previousComments;
  const sharesChange = currentShares - previousShares;
  const viewsChange = currentViews - previousViews;

  const calculatePercentage = (current: number, previous: number) => {
    if (previous === 0) return undefined;
    return ((current - previous) / previous) * 100;
  };

  const likesPercentage = calculatePercentage(currentLikes, previousLikes);
  const commentsPercentage = calculatePercentage(
    currentComments,
    previousComments,
  );
  const sharesPercentage = calculatePercentage(currentShares, previousShares);
  const viewsPercentage = calculatePercentage(currentViews, previousViews);

  const hasAnyChanges =
    likesChange !== 0 ||
    commentsChange !== 0 ||
    sharesChange !== 0 ||
    viewsChange !== 0;

  return (
    <Card className="flex w-full flex-col !gap-3 !p-4 !shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <CardTitle className="!leading-9 !-tracking-wide">
              Engagement Changes
            </CardTitle>
            {snapshot.snapshot_date && (
              <p className="text-xs text-neutral-500">
                Last checked:{" "}
                {new Date(snapshot.snapshot_date).toLocaleString()}
              </p>
            )}
          </div>
          {hasAnyChanges && (
            <div className="flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
              <i className="ri-notification-line"></i>
              Changes Detected
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {!hasAnyChanges ? (
          <p className="text-sm text-neutral-500">{snapshot.message}</p>
        ) : (
          <div className="flex flex-col gap-3">
            <ChangeIndicatorCompact
              label="Likes"
              value={currentLikes}
              change={likesChange}
              changePercentage={likesPercentage}
              icon={
                <div className="flex items-center">
                  <FbLikeIcon width={16} height={16} className="z-10" />
                  <FbLoveIcon
                    width={16}
                    height={16}
                    className="z-20 -ml-2 rounded-full"
                  />
                </div>
              }
            />
            <ChangeIndicatorCompact
              label="Comments"
              value={currentComments}
              change={commentsChange}
              changePercentage={commentsPercentage}
              icon={<CommentsIcon width={16} height={16} />}
            />
            <ChangeIndicatorCompact
              label="Shares"
              value={currentShares}
              change={sharesChange}
              changePercentage={sharesPercentage}
              icon={<ShareIcon width={16} height={16} />}
            />
            {currentViews > 0 && (
              <ChangeIndicatorCompact
                label="Views"
                value={currentViews}
                change={viewsChange}
                changePercentage={viewsPercentage}
                icon={<i className="ri-eye-line text-[16px]"></i>}
              />
            )}

            {snapshot.previous_snapshot_date && (
              <p className="pt-1 text-xs text-neutral-400">
                Compared with{" "}
                {new Date(snapshot.previous_snapshot_date).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
